import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api } from '~/lib/api-client';
import { classifyNotificationHref, operatorRunIdFromHref } from '~/lib/notification-href';
import { openOperatorPanel } from '~/lib/stores/operator-panel-store';
import { notificationKeys, cronKeys } from '~/lib/query-keys';
export { notificationKeys };

export type NotificationStatus = 'pending' | 'resolved' | 'dismissed';
export type NotificationSeverity = 'info' | 'warning' | 'error';

export interface NotificationAction {
  label: string;
  href: string;
  method?: 'GET' | 'POST';
  payload?: Record<string, unknown>;
}

export interface UserNotification {
  id: number;
  kind: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  action: NotificationAction | null;
  status: NotificationStatus;
  dedupeKey: string | null;
  createdAt: string;
  resolvedAt: string | null;
  // FRONT-1990 round 2: the desktop-operator run's final screenshot, when the
  // run reached a step that captured one. Null otherwise — never absent, so
  // every reader can check it with a plain truthiness test.
  imageUrl: string | null;
}

function isAuthError(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 401;
}

export function useNotifications(status: NotificationStatus = 'pending') {
  return useQuery({
    queryKey: notificationKeys.list(status),
    queryFn: async () => {
      const r = await api.get<{ items: UserNotification[] }>(
        `/api/notifications?status=${status}`,
      );
      return r.items ?? [];
    },
    staleTime: 30_000,
    // SSE invalidates on `notification-changed` (see use-sse.ts).
    // 5-minute fallback for dropped sockets; auth errors stop polling.
    refetchInterval: (q) => (isAuthError(q.state.error) ? false : 5 * 60_000),
    retry: (_count, err) => !isAuthError(err),
  });
}

/**
 * FRONT-2486: the one label for "I've handled this", on the bell and the page
 * alike — they used to say "Done" and "Mark resolved" for the same action.
 */
export const RESOLVE_LABEL = 'Mark resolved';

/**
 * FRONT-2486: the bell badge's text. It reads the same server count as the
 * popover header and the page — only the display is capped, and at 99, so a
 * badge beside "248 pending" no longer reads "9+".
 */
export function pendingBadgeLabel(pending: number): string {
  return pending > 99 ? '99+' : String(pending);
}

/**
 * The pending total every surface shows (badge, popover, page). Always the
 * server's count — never the length of a fetched list, which is capped at one
 * page (50) and silently disagreed with it.
 */
export function useNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.count(),
    queryFn: () => api.get<{ pending: number }>('/api/notifications/count'),
    staleTime: 30_000,
    // SSE invalidates on `notification-changed` (see use-sse.ts).
    // 5-minute fallback for dropped sockets; auth errors stop polling.
    refetchInterval: (q) => (isAuthError(q.state.error) ? false : 5 * 60_000),
    retry: (_count, err) => !isAuthError(err),
  });
}

export function useResolveNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/api/notifications/${id}/resolve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/api/notifications/${id}/dismiss`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Run a notification's action, then mark it resolved. The link decides the
 * mechanism — see `~/lib/notification-href` for the classifier and its corpus:
 *  - a server action (`/api/…`) is invoked via the api client so the
 *    401-refresh logic applies (re-auth, resolve security, etc.);
 *  - an in-app path (e.g. `/reflect`) navigates the SPA. Without this branch a
 *    link-style action silently did a background GET to the route and resolved
 *    without ever navigating (FRONT-488);
 *  - a desktop-operator run link (`/operator/runs/<uuid>`, FRONT-1990 round 2)
 *    opens the operator panel in place via `openOperatorPanel` instead of
 *    navigating — the run has no page of its own;
 *  - an absolute http(s) link — which the broadcast validator explicitly
 *    allows — leaves the app via a real document load, never the router
 *    (FRONT-1764);
 *  - anything else is refused rather than followed.
 */
export function useRunNotificationAction() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (n: UserNotification) => {
      if (!n.action) throw new Error('No action');
      const { href, method = 'GET', payload } = n.action;
      // FRONT-1764: react-router 7.18 validates navigation targets, so an
      // absolute URL handed to navigate() THROWS rather than landing on a
      // nonsense internal path the way 7.14 did. The broadcast validator
      // explicitly allows absolute http(s) links, so that case is reachable —
      // and no call site of this mutation passes an onError, which would make
      // the throw invisible. Route by kind instead of by prefix.
      const kind = classifyNotificationHref(href);
      if (kind === 'unsupported') {
        throw new Error(`Notification action link is not supported: ${href}`);
      }
      if (kind === 'api') {
        if (method === 'POST') {
          await api.post(href, payload);
        } else {
          await api.get(href);
        }
      } else if (kind === 'internal') {
        navigate(href);
      } else if (kind === 'operator-run') {
        const runId = operatorRunIdFromHref(href);
        if (runId) openOperatorPanel({ runId });
      }
      // Resolve BEFORE any external hand-off: window.location.assign starts a
      // full document load, which cancels a request still in flight — the
      // notification would come back unresolved on the next page.
      await api.post(`/api/notifications/${n.id}/resolve`);
      if (kind === 'external') window.location.assign(href);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      qc.invalidateQueries({ queryKey: ['security'] });
      qc.invalidateQueries({ queryKey: cronKeys.all });
    },
  });
}
